/**
 * Store Factory - Creates isolated store instances for widget isolation
 *
 * Each widget instance gets its own isolated set of stores to prevent
 * state collisions when multiple widgets are embedded on the same page.
 * This factory creates fresh Zustand stores for each widget instance.
 */

import type {
  DeepgramTranscript,
  RecordingState,
  RefinementState,
  UserSession,
  WordWithAlternatives,
} from '@refinevoice/shared';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { InputMode, TextSegment } from '../types/hybridInput';

// Store interfaces
interface TranscriptStore {
  // Widget ID for debugging
  widgetId: string;

  // State
  recordingState: RecordingState;
  transcript: string;
  interimTranscript: string;
  wordsWithAlternatives: WordWithAlternatives[];
  selectedWordIndex: number | null;

  // Hybrid input state
  inputMode: InputMode;
  voiceCursorPosition: number;
  keyboardCursorPosition: number;
  textSegments: TextSegment[];
  selectedText: {
    text: string;
    startIndex: number;
    endIndex: number;
  } | null;

  // Actions
  setRecordingState: (state: RecordingState) => void;
  setTranscript: (text: string) => void;
  appendTranscript: (text: string, deepgramData?: DeepgramTranscript) => void;
  setInterimTranscript: (text: string) => void;
  setWordsWithAlternatives: (words: WordWithAlternatives[]) => void;
  updateWord: (index: number, newWord: string) => void;
  deleteWord: (index: number) => void;
  setSelectedWordIndex: (index: number | null) => void;

  // Hybrid input actions
  setInputMode: (mode: InputMode) => void;
  setVoiceCursorPosition: (position: number) => void;
  setKeyboardCursorPosition: (position: number) => void;
  insertTextAtCursor: (text: string, source: InputMode, deepgramData?: DeepgramTranscript) => void;
  replaceTextRange: (startIndex: number, endIndex: number, text: string, source: InputMode) => void;
  setSelectedText: (
    selection: { text: string; startIndex: number; endIndex: number } | null
  ) => void;

  reset: () => void;
}

interface RecordingStore {
  widgetId: string;
  recordingState: RecordingState;
  transcript: string;
  refinedText: string;
  interimTranscript: string;
  showWaveform: boolean;
  wordsWithAlternatives: WordWithAlternatives[];
  selectedWordIndex: number | null;
  isRerecording: boolean;
  rerecordWordIndex: number | null;
  refinementState: RefinementState;
  refinementError: string | null;
  previousRefinedText: string | null;
  errorNotification: { title: string; message: string; type: 'error' | 'warning' | 'info' } | null;
  deepgramToken: string | null;
  deepgramSocket: WebSocket | null;
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error';
  isUsingLocalTranscription: boolean;
  isRecordingLocked: boolean;
  userSession: UserSession | null;

  setRecordingState: (state: RecordingState) => void;
  setTranscript: (text: string) => void;
  appendTranscript: (text: string, deepgramData?: DeepgramTranscript) => void;
  setRefinedText: (text: string) => void;
  setInterimTranscript: (text: string) => void;
  setRefinementState: (state: RefinementState) => void;
  setRefinementError: (error: string | null) => void;
  setPreviousRefinedText: (text: string | null) => void;
  setErrorNotification: (
    notification: { title: string; message: string; type: 'error' | 'warning' | 'info' } | null
  ) => void;
  showError: (title: string, message: string, type?: 'error' | 'warning' | 'info') => void;
  setDeepgramToken: (token: string | null) => void;
  setDeepgramSocket: (socket: WebSocket | null) => void;
  setConnectionState: (state: 'disconnected' | 'connecting' | 'connected' | 'error') => void;
  setIsUsingLocalTranscription: (isLocal: boolean) => void;
  setWordsWithAlternatives: (words: WordWithAlternatives[]) => void;
  updateWord: (index: number, newWord: string) => void;
  deleteWord: (index: number) => void;
  setSelectedWordIndex: (index: number | null) => void;
  setIsRerecording: (isRerecording: boolean) => void;
  setRerecordWordIndex: (index: number | null) => void;
  replaceWord: (index: number, newText: string, deepgramData?: DeepgramTranscript) => void;
  setUserSession: (session: UserSession | null) => void;
  setShowWaveform: (show: boolean) => void;
  setIsRecordingLocked: (isLocked: boolean) => void;
  reset: () => void;
}

interface RerecordStore {
  widgetId: string;
  isRerecording: boolean;
  rerecordWordIndex: number | null;

  setIsRerecording: (isRerecording: boolean) => void;
  setRerecordWordIndex: (index: number | null) => void;
  replaceWord: (index: number, newText: string, deepgramData?: DeepgramTranscript) => void;
  reset: () => void;
}

interface RefinementStore {
  widgetId: string;
  refinementState: RefinementState;
  refinedText: string;
  refinementError: string | null;
  previousRefinedText: string | null;

  setRefinementState: (state: RefinementState) => void;
  setRefinedText: (text: string) => void;
  setRefinementError: (error: string | null) => void;
  setPreviousRefinedText: (text: string | null) => void;
  reset: () => void;
}

interface AppStateStore {
  widgetId: string;
  errorNotification: { title: string; message: string; type: 'error' | 'warning' | 'info' } | null;
  userSession: UserSession | null;
  showWaveform: boolean;
  isRecordingLocked: boolean;

  setErrorNotification: (
    notification: { title: string; message: string; type: 'error' | 'warning' | 'info' } | null
  ) => void;
  showError: (title: string, message: string, type?: 'error' | 'warning' | 'info') => void;
  setUserSession: (session: UserSession | null) => void;
  setShowWaveform: (show: boolean) => void;
  setIsRecordingLocked: (isLocked: boolean) => void;
  reset: () => void;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

interface ConnectionStore {
  widgetId: string;
  deepgramToken: string | null;
  deepgramSocket: WebSocket | null;
  connectionState: ConnectionState;
  isUsingLocalTranscription: boolean;

  setDeepgramToken: (token: string | null) => void;
  setDeepgramSocket: (socket: WebSocket | null) => void;
  setConnectionState: (state: ConnectionState) => void;
  setIsUsingLocalTranscription: (isLocal: boolean) => void;
  reset: () => void;
}

export interface WidgetStores {
  useTranscriptStore: UseBoundStore<StoreApi<TranscriptStore>>;
  useRecordingStore: UseBoundStore<StoreApi<RecordingStore>>;
  useRerecordStore: UseBoundStore<StoreApi<RerecordStore>>;
  useRefinementStore: UseBoundStore<StoreApi<RefinementStore>>;
  useAppStateStore: UseBoundStore<StoreApi<AppStateStore>>;
  useConnectionStore: UseBoundStore<StoreApi<ConnectionStore>>;
}

/**
 * Creates isolated store instances for a widget
 */
export function createWidgetStores(widgetId: string): WidgetStores {
  // Transcript Store
  const useTranscriptStore = create<TranscriptStore>((set) => {
    const initialState = {
      widgetId,
      recordingState: 'idle' as RecordingState,
      transcript: '',
      interimTranscript: '',
      wordsWithAlternatives: [] as WordWithAlternatives[],
      selectedWordIndex: null as number | null,
      inputMode: 'voice' as InputMode,
      voiceCursorPosition: 0,
      keyboardCursorPosition: 0,
      textSegments: [] as TextSegment[],
      selectedText: null as { text: string; startIndex: number; endIndex: number } | null,
    };

    return {
      ...initialState,

      setRecordingState: (recordingState) => set({ recordingState }),
      setTranscript: (transcript) => set({ transcript }),
      appendTranscript: (text, deepgramData) =>
        set((state) => {
          const newTranscript = state.transcript ? `${state.transcript} ${text}` : text;

          if (!deepgramData?.words || deepgramData.words.length === 0) {
            return {
              transcript: newTranscript,
              wordsWithAlternatives: state.wordsWithAlternatives,
            };
          }

          const wordsWithAlternatives = deepgramData.words.map((word, index) => {
            const alternatives =
              deepgramData.alternatives
                ?.slice(1)
                .map((alt) => {
                  const altWord = alt.words?.find(
                    (w) =>
                      Math.abs(w.start - word.start) < 0.5 &&
                      Math.abs(w.end - word.end) < 0.5 &&
                      w.word !== word.word
                  );
                  return altWord?.punctuated_word || altWord?.word;
                })
                .filter((alt): alt is string => alt !== undefined && alt !== word.word) || [];

            return {
              word: word.punctuated_word || word.word,
              confidence: word.confidence,
              alternatives,
              startIndex: state.transcript.length + (state.transcript ? 1 : 0),
              endIndex: state.transcript.length + text.length,
              originalIndex: state.wordsWithAlternatives.length + index,
            };
          });

          return {
            transcript: newTranscript,
            wordsWithAlternatives: [...state.wordsWithAlternatives, ...wordsWithAlternatives],
          };
        }),
      setInterimTranscript: (interimTranscript) => set({ interimTranscript }),
      setWordsWithAlternatives: (wordsWithAlternatives) => set({ wordsWithAlternatives }),
      updateWord: (index, newWord) =>
        set((state) => {
          const updatedWords = [...state.wordsWithAlternatives];
          if (updatedWords[index]) {
            updatedWords[index] = { ...updatedWords[index], word: newWord };
          }
          const newTranscript = updatedWords.map((w) => w.word).join(' ');
          return { wordsWithAlternatives: updatedWords, transcript: newTranscript };
        }),
      deleteWord: (index) =>
        set((state) => {
          const updatedWords = state.wordsWithAlternatives.filter((_, i) => i !== index);
          const newTranscript = updatedWords.map((w) => w.word).join(' ');
          return {
            wordsWithAlternatives: updatedWords,
            transcript: newTranscript,
            selectedWordIndex: null,
          };
        }),
      setSelectedWordIndex: (selectedWordIndex) => set({ selectedWordIndex }),
      setInputMode: (inputMode) => set({ inputMode }),
      setVoiceCursorPosition: (voiceCursorPosition) => set({ voiceCursorPosition }),
      setKeyboardCursorPosition: (keyboardCursorPosition) => set({ keyboardCursorPosition }),
      insertTextAtCursor: (text, source, deepgramData) =>
        set((state) => {
          const insertPosition =
            source === 'voice' ? state.voiceCursorPosition : state.keyboardCursorPosition;
          const beforeText = state.transcript.slice(0, insertPosition);
          const afterText = state.transcript.slice(insertPosition);
          const newTranscript = beforeText + text + afterText;

          const newSegment: TextSegment = {
            text,
            startIndex: insertPosition,
            endIndex: insertPosition + text.length,
            inputSource: source,
            confidence: deepgramData?.confidence,
            alternatives: deepgramData?.alternatives?.map((alt) => alt.transcript) || [],
          };

          const updatedSegments = state.textSegments
            .filter((segment) => segment.endIndex <= insertPosition)
            .concat([newSegment])
            .concat(
              state.textSegments
                .filter((segment) => segment.startIndex > insertPosition)
                .map((segment) => ({
                  ...segment,
                  startIndex: segment.startIndex + text.length,
                  endIndex: segment.endIndex + text.length,
                }))
            );

          const newVoiceCursorPosition =
            source === 'voice'
              ? insertPosition + text.length
              : state.voiceCursorPosition + text.length;
          const newKeyboardCursorPosition =
            source === 'keyboard'
              ? insertPosition + text.length
              : state.keyboardCursorPosition + text.length;

          return {
            transcript: newTranscript,
            textSegments: updatedSegments,
            voiceCursorPosition: newVoiceCursorPosition,
            keyboardCursorPosition: newKeyboardCursorPosition,
            inputMode: source === 'voice' ? 'voice' : source === 'keyboard' ? 'keyboard' : 'mixed',
          };
        }),
      replaceTextRange: (startIndex, endIndex, text, source) =>
        set((state) => {
          const beforeText = state.transcript.slice(0, startIndex);
          const afterText = state.transcript.slice(endIndex);
          const newTranscript = beforeText + text + afterText;
          const lengthDiff = text.length - (endIndex - startIndex);

          const newSegment: TextSegment = {
            text,
            startIndex,
            endIndex: startIndex + text.length,
            inputSource: source,
          };

          const updatedSegments = state.textSegments
            .filter((segment) => segment.endIndex <= startIndex || segment.startIndex >= endIndex)
            .map((segment) => {
              if (segment.endIndex <= startIndex) return segment;
              return {
                ...segment,
                startIndex: segment.startIndex + lengthDiff,
                endIndex: segment.endIndex + lengthDiff,
              };
            })
            .concat([newSegment]);

          const newVoiceCursorPosition =
            state.voiceCursorPosition > startIndex
              ? state.voiceCursorPosition + lengthDiff
              : state.voiceCursorPosition;
          const newKeyboardCursorPosition =
            state.keyboardCursorPosition > startIndex
              ? state.keyboardCursorPosition + lengthDiff
              : state.keyboardCursorPosition;

          return {
            transcript: newTranscript,
            textSegments: updatedSegments,
            voiceCursorPosition: newVoiceCursorPosition,
            keyboardCursorPosition: newKeyboardCursorPosition,
            inputMode: source === 'voice' ? 'voice' : source === 'keyboard' ? 'keyboard' : 'mixed',
          };
        }),
      setSelectedText: (selectedText) => set({ selectedText }),
      reset: () => set(initialState),
    };
  });

  // Recording Store
  const useRecordingStore = create<RecordingStore>((set) => {
    const initialState = {
      widgetId,
      userSession: null as UserSession | null,
      recordingState: 'idle' as RecordingState,
      transcript: '',
      refinedText: '',
      interimTranscript: '',
      showWaveform: false,
      wordsWithAlternatives: [] as WordWithAlternatives[],
      selectedWordIndex: null as number | null,
      isRerecording: false,
      rerecordWordIndex: null as number | null,
      refinementState: 'idle' as RefinementState,
      refinementError: null as string | null,
      previousRefinedText: null as string | null,
      errorNotification: null as {
        title: string;
        message: string;
        type: 'error' | 'warning' | 'info';
      } | null,
      deepgramToken: null as string | null,
      deepgramSocket: null as WebSocket | null,
      connectionState: 'disconnected' as 'disconnected' | 'connecting' | 'connected' | 'error',
      isUsingLocalTranscription: false,
      isRecordingLocked: false,
    };

    return {
      ...initialState,
      setRecordingState: (recordingState) => set({ recordingState }),
      setTranscript: (transcript) => set({ transcript }),
      appendTranscript: (text, deepgramData) =>
        set((state) => {
          const newTranscript = state.transcript ? `${state.transcript} ${text}` : text;

          if (!deepgramData?.words || deepgramData.words.length === 0) {
            return {
              transcript: newTranscript,
              wordsWithAlternatives: state.wordsWithAlternatives,
            };
          }

          const wordsWithAlternatives = deepgramData.words.map((word, index) => {
            const alternatives =
              deepgramData.alternatives
                ?.slice(1)
                .map((alt) => {
                  const altWord = alt.words?.find(
                    (w) =>
                      Math.abs(w.start - word.start) < 0.5 &&
                      Math.abs(w.end - word.end) < 0.5 &&
                      w.word !== word.word
                  );
                  return altWord?.punctuated_word || altWord?.word;
                })
                .filter((alt): alt is string => alt !== undefined && alt !== word.word) || [];

            return {
              word: word.punctuated_word || word.word,
              confidence: word.confidence,
              alternatives,
              startIndex: state.transcript.length + (state.transcript ? 1 : 0),
              endIndex: state.transcript.length + text.length,
              originalIndex: state.wordsWithAlternatives.length + index,
            };
          });

          return {
            transcript: newTranscript,
            wordsWithAlternatives: [...state.wordsWithAlternatives, ...wordsWithAlternatives],
          };
        }),
      setRefinedText: (refinedText) => set({ refinedText }),
      setInterimTranscript: (interimTranscript) => set({ interimTranscript }),
      setRefinementState: (refinementState) => set({ refinementState }),
      setRefinementError: (refinementError) => set({ refinementError }),
      setPreviousRefinedText: (previousRefinedText) => set({ previousRefinedText }),
      setErrorNotification: (errorNotification) => set({ errorNotification }),
      showError: (title, message, type = 'error') =>
        set({ errorNotification: { title, message, type } }),
      setDeepgramToken: (deepgramToken) => set({ deepgramToken }),
      setDeepgramSocket: (deepgramSocket) => set({ deepgramSocket }),
      setConnectionState: (connectionState) => set({ connectionState }),
      setIsUsingLocalTranscription: (isUsingLocalTranscription) =>
        set({ isUsingLocalTranscription }),
      setWordsWithAlternatives: (wordsWithAlternatives) => set({ wordsWithAlternatives }),
      updateWord: (index, newWord) =>
        set((state) => {
          const updatedWords = [...state.wordsWithAlternatives];
          if (updatedWords[index]) {
            updatedWords[index] = { ...updatedWords[index], word: newWord };
          }
          const newTranscript = updatedWords.map((w) => w.word).join(' ');
          return { wordsWithAlternatives: updatedWords, transcript: newTranscript };
        }),
      deleteWord: (index) =>
        set((state) => {
          const updatedWords = state.wordsWithAlternatives.filter((_, i) => i !== index);
          const newTranscript = updatedWords.map((w) => w.word).join(' ');
          return {
            wordsWithAlternatives: updatedWords,
            transcript: newTranscript,
            selectedWordIndex: null,
          };
        }),
      setSelectedWordIndex: (selectedWordIndex) => set({ selectedWordIndex }),
      setIsRerecording: (isRerecording) => set({ isRerecording }),
      setRerecordWordIndex: (rerecordWordIndex) => set({ rerecordWordIndex }),
      replaceWord: (index, newText, deepgramData) =>
        set((state) => {
          if (deepgramData?.words && deepgramData.words.length > 0) {
            const newWords = deepgramData.words.map((word, i) => ({
              word: word.punctuated_word || word.word,
              confidence: word.confidence,
              alternatives: [],
              startIndex: state.wordsWithAlternatives[index]?.startIndex || 0,
              endIndex: state.wordsWithAlternatives[index]?.endIndex || 0,
              originalIndex: index + i,
            }));

            const updatedWords = [
              ...state.wordsWithAlternatives.slice(0, index),
              ...newWords,
              ...state.wordsWithAlternatives.slice(index + 1),
            ];

            const newTranscript = updatedWords.map((w) => w.word).join(' ');

            return {
              wordsWithAlternatives: updatedWords,
              transcript: newTranscript,
              isRerecording: false,
              rerecordWordIndex: null,
            };
          }
          const words = state.transcript.split(' ');
          words[index] = newText;
          const newTranscript = words.join(' ');

          return { transcript: newTranscript, isRerecording: false, rerecordWordIndex: null };
        }),
      setUserSession: (userSession) => set({ userSession }),
      setShowWaveform: (showWaveform) => set({ showWaveform }),
      setIsRecordingLocked: (isRecordingLocked) => set({ isRecordingLocked }),
      reset: () => set(initialState),
    };
  });

  // Rerecord Store
  const useRerecordStore = create<RerecordStore>((set) => {
    const initialState = {
      widgetId,
      isRerecording: false,
      rerecordWordIndex: null as number | null,
    };

    return {
      ...initialState,
      setIsRerecording: (isRerecording) => set({ isRerecording }),
      setRerecordWordIndex: (rerecordWordIndex) => set({ rerecordWordIndex }),
      replaceWord: (index, newText, deepgramData) => {
        const transcriptStore = useTranscriptStore.getState();
        const { wordsWithAlternatives, transcript } = transcriptStore;

        if (deepgramData?.words && deepgramData.words.length > 0) {
          const newWords: WordWithAlternatives[] = deepgramData.words.map((word, i) => ({
            word: word.punctuated_word || word.word,
            confidence: word.confidence,
            alternatives: [],
            startIndex: wordsWithAlternatives[index]?.startIndex || 0,
            endIndex: wordsWithAlternatives[index]?.endIndex || 0,
            originalIndex: index + i,
          }));

          const updatedWords = [
            ...wordsWithAlternatives.slice(0, index),
            ...newWords,
            ...wordsWithAlternatives.slice(index + 1),
          ];

          const newTranscript = updatedWords.map((w) => w.word).join(' ');

          transcriptStore.setWordsWithAlternatives(updatedWords);
          transcriptStore.setTranscript(newTranscript);
        } else {
          const words = transcript.split(' ');
          words[index] = newText;
          const newTranscript = words.join(' ');

          transcriptStore.setTranscript(newTranscript);
        }

        set({ isRerecording: false, rerecordWordIndex: null });
      },
      reset: () => set(initialState),
    };
  });

  // Refinement Store
  const useRefinementStore = create<RefinementStore>((set) => {
    const initialState = {
      widgetId,
      refinementState: 'idle' as RefinementState,
      refinedText: '',
      refinementError: null as string | null,
      previousRefinedText: null as string | null,
    };

    return {
      ...initialState,
      setRefinementState: (refinementState) => set({ refinementState }),
      setRefinedText: (refinedText) => set({ refinedText }),
      setRefinementError: (refinementError) => set({ refinementError }),
      setPreviousRefinedText: (previousRefinedText) => set({ previousRefinedText }),
      reset: () => set(initialState),
    };
  });

  // AppState Store
  const useAppStateStore = create<AppStateStore>((set) => {
    const initialState = {
      widgetId,
      errorNotification: null as {
        title: string;
        message: string;
        type: 'error' | 'warning' | 'info';
      } | null,
      userSession: null as UserSession | null,
      showWaveform: false,
      isRecordingLocked: false,
    };

    return {
      ...initialState,
      setErrorNotification: (errorNotification) => set({ errorNotification }),
      showError: (title, message, type = 'error') =>
        set({ errorNotification: { title, message, type } }),
      setUserSession: (userSession) => set({ userSession }),
      setShowWaveform: (showWaveform) => set({ showWaveform }),
      setIsRecordingLocked: (isRecordingLocked) => set({ isRecordingLocked }),
      reset: () => set(initialState),
    };
  });

  // Connection Store
  const useConnectionStore = create<ConnectionStore>((set) => {
    const initialState = {
      widgetId,
      deepgramToken: null as string | null,
      deepgramSocket: null as WebSocket | null,
      connectionState: 'disconnected' as ConnectionState,
      isUsingLocalTranscription: false,
    };

    return {
      ...initialState,
      setDeepgramToken: (deepgramToken) => set({ deepgramToken }),
      setDeepgramSocket: (deepgramSocket) => set({ deepgramSocket }),
      setConnectionState: (connectionState) => set({ connectionState }),
      setIsUsingLocalTranscription: (isUsingLocalTranscription) =>
        set({ isUsingLocalTranscription }),
      reset: () => set(initialState),
    };
  });

  return {
    useTranscriptStore,
    useRecordingStore,
    useRerecordStore,
    useRefinementStore,
    useAppStateStore,
    useConnectionStore,
  };
}
